/**
 * A date, end to end: you ask someone (they can say yes, no, or "another time"), it goes in the diary for a
 * restaurant and a time, you turn up (on time, washed, dressed: first impressions count), you talk, you order,
 * somebody pays, and the evening ends however you end it. Next morning, they text (or they don't).
 *
 * The SPARK (0..1) is the evening: how you came in, how the talk went, how the bill went. What you can ask for at
 * the end, and how they answer, follows from it. All of it visible to the player as it happens, never hidden odds.
 */
import { NPC_KIN } from './kin.ts';
import { advance, dayOf, message, spend, type LifeEvent, type LifeState } from './life.ts';
import { hereCountry, localPrice, nudge } from './character.ts';
import { homeCountry } from './travel.ts';
import { isFamily, isPartner } from './lifecycle.ts';
import { REGIONS } from './spawn.ts';
import { NAMES } from './content/names.ts';
import { snackPrice } from './beaches.ts';
import { Rng } from './rng.ts';

export interface DatePlan {
  name: string;
  /** when (game minutes) */
  at: number;
  /** the restaurant, as the game names it */
  venue: string;
  /** invited: sent to a real player, waiting on their answer (an NPC answers on the spot) */
  state: 'invited' | 'booked' | 'done' | 'missed' | 'declined';
  /** who the other person is: an NPC in your life, or another player (multiplayer) */
  with?: 'npc' | 'player';
  /** where: the restaurant (unset: older saves), the bar, the beach, the park */
  spot?: OutingSpot;
}
/** the places a date or a hangout can be (the game lists only the ones this city has) */
export type OutingSpot = 'restaurant' | 'lounge' | 'beach' | 'park';
/** out in the day (the beach, the park: the park's last entry is at four) or the evening */
const daytime = (spot?: OutingSpot) => spot === 'beach' || spot === 'park';

const DAY = 1440;
const rngFor = (s: LifeState, salt: number) => new Rng((s.seed ^ Math.imul(salt + 1, 0x85ebca6b)) >>> 0);
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/** Someone's sex from their first name (every region's name lists); unknown names by a stable coin flip. */
export function sexOfName(name: string): 'm' | 'f' {
  const first = name.split(' ')[0];
  const last = name.split(' ').slice(-1)[0];
  for (const c of Object.values(REGIONS)) for (const g of c.names) {
    if (g.m.includes(first) || g.m.includes(last)) return 'm';
    if (g.f.includes(first) || g.f.includes(last)) return 'f';
  }
  for (const n of Object.values(NAMES)) { if (n.m.includes(first) || n.m.includes(last)) return 'm'; if (n.f.includes(first) || n.f.includes(last)) return 'f'; }
  return [...name].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7) % 2 ? 'm' : 'f';
}

/** Where someone's family comes from, from their name (the surname first: "Diallo" is west_african wherever they live); null if no list has it. */
export function heritageOfName(name: string): string | null {
  const parts = name.split(' ');
  for (const pick of [(g: { surname: string[] }) => parts.some((p) => g.surname.includes(p)), (g: { m: string[]; f: string[] }) => g.m.includes(parts[0]) || g.f.includes(parts[0])])
    for (const c of Object.values(REGIONS)) for (const g of c.names) if (pick(g)) return g.id;
  return null;
}

export const datesOf = (s: LifeState): DatePlan[] => (s.dates ??= []);
/** a date that's on now (from half an hour before to an hour and a half after the time), or null */
export function dateNow(s: LifeState): DatePlan | null {
  return datesOf(s).find((d) => d.state === 'booked' && s.minutes >= d.at - 30 && s.minutes <= d.at + 90) ?? null;
}
/** dates still to come */
export const datesAhead = (s: LifeState) => datesOf(s).filter((d) => d.state === 'booked' && d.at + 90 >= s.minutes).sort((a, b) => a.at - b.at);

/** Who you can ask: not family. (Your partner too: a date night.) */
/** (an NPC on a date: archived with the rest of NPC romance, kin.ts NPC_KIN; a player's date is asked another way) */
export const canAskOnDate = (relation: string) => NPC_KIN && !isFamily(relation) && relation !== 'Ex';

/** The evenings you can suggest: tonight (if it's not too late), tomorrow, Saturday; at eight (the beach or the park: at two in the afternoon). */
export function dateSlots(s: LifeState, spot?: OutingSpot): { label: string; at: number }[] {
  const today = dayOf(s.minutes), out: { label: string; at: number }[] = [];
  const h = daytime(spot) ? 14 : 20, t = `${h}:00`;
  // right now: as soon as you can both get there (about 45 minutes, to the next quarter hour), any time from 8:00 to 22:00
  const soon = Math.ceil((s.minutes + 45) / 15) * 15, sh = (soon % DAY) / 60;
  if (sh >= 8 && sh <= 22) out.push({ label: `Right now (meet at ${String(Math.floor(sh)).padStart(2, '0')}:${String(soon % 60).padStart(2, '0')})`, at: soon });
  if (s.minutes % DAY < (h - 2) * 60 && Math.abs(soon - (today * DAY + h * 60)) > 60) out.push({ label: `${daytime(spot) ? 'Today' : 'Tonight'}, ${t}`, at: today * DAY + h * 60 });
  out.push({ label: `Tomorrow, ${t}`, at: (today + 1) * DAY + h * 60 });
  let sat = today + 2; while (sat % 7 !== 5) sat++;
  out.push({ label: `Saturday, ${t}`, at: sat * DAY + h * 60 });
  return out;
}

/**
 * Ask. Closer people say yes more; strangers you met last night, less; a partner almost always (date night).
 * A "maybe later" keeps the door open.
 */
export function askOnDate(s: LifeState, name: string, at: number, venue: string, spot?: OutingSpot): { answer: 'yes' | 'no' | 'later'; line: string; events: LifeEvent[] } {
  const p = s.contacts.find((c) => c.name === name);
  const events = advance(s, 5);
  if (!p || !canAskOnDate(p.relation)) return { answer: 'no', line: '…', events };
  if (datesOf(s).some((d) => d.state === 'booked' && d.name === name)) return { answer: 'no', line: '"We already have plans!"', events };
  const r = rngFor(s, s.minutes + name.length * 7);
  const partner = isPartner(p.relation);
  const chance = partner ? 0.92 : clamp01(0.12 + p.closeness / 90 + s.skills.social / 400 + (s.dressedDay === dayOf(s.minutes) ? 0.04 : 0));
  const roll = r.next();
  const first = name.split(' ')[0];
  if (roll < chance) {
    datesOf(s).push({ name, at, venue, state: 'booked', ...(spot && spot !== 'restaurant' ? { spot } : {}) });
    message(s, name, partner ? `Date night 😍 ${venue.split(',')[0]}, don't be late` : r.pick([`Okay 🙂 ${venue.split(',')[0]} it is. Don't be late.`, `Yes! See you at ${venue.split(',')[0]}.`, 'Sounds nice. I\'ll be there.']), events);
    return { answer: 'yes', line: partner ? `"Date night? Finally."` : r.pick(['"I\'d like that."', '"Okay. Yes."', '"About time you asked."']), events };
  }
  if (roll < chance + 0.25) return { answer: 'later', line: r.pick([`"Not that day. Ask me again?"`, `"I'm busy that night… another time, maybe."`]), events };
  p.closeness = Math.max(0, p.closeness - 4);
  return { answer: 'no', line: r.pick(['"I don\'t think so. Sorry."', `"You're sweet, ${first}. But no."`, '"I\'m not really looking for anything right now."']), events };
}

/**
 * Inviting a real player: it waits in both diaries as "invited" until they answer on their side (the backend
 * carries it there and back). Same plan as an NPC date; only who says yes differs.
 */
export function inviteToDate(s: LifeState, name: string, at: number, venue: string): DatePlan {
  const d: DatePlan = { name, at, venue, state: 'invited', with: 'player' };
  datesOf(s).push(d);
  return d;
}
/** Their answer came back (or, on their side, you answered theirs). */
export function answerInvite(d: DatePlan, yes: boolean) { d.state = yes ? 'booked' : 'declined'; }

/**
 * Walking in: on time or not, washed or not, dressed for it or not. 0..1, and what they notice.
 */
export function firstImpression(s: LifeState, lateMin: number): { impression: number; notes: string[] } {
  const notes: string[] = [];
  let k = 0.55;
  if (lateMin <= 5) { k += 0.12; notes.push('On time.'); } else if (lateMin <= 20) { k -= 0.08; notes.push(`${lateMin} minutes late.`); } else { k -= 0.25; notes.push(`${lateMin} minutes late. They noticed.`); }
  if (s.dressedDay === dayOf(s.minutes)) { k += 0.15; notes.push('You dressed up.'); } else notes.push('You came as you were.');
  if (s.needs.hygiene < 35) { k -= 0.2; notes.push('You could have showered.'); } else if (s.needs.hygiene > 70) k += 0.05;
  return { impression: clamp01(k), notes };
}

/** First-date conversation: what they bring up, three ways to answer (how it lands, and why). */
export function dateTopics(s: LifeState): { line: string; answers: [string, number, string, string?][] }[] {
  const c = hereCountry(s);
  const local = c === 'nigeria' ? ['"So… Lagos or Abuja? Choose wisely."', '"Lagos. The energy."', '"Neither. Ibadan."']
    : c === 'france' ? ['"Paris or the countryside, if you had to live in one?"', '"Paris. I can\'t live without the noise."', '"A village with one good bakery."']
      : c === 'usa' ? ['"East coast or West coast?"', '"East. Seasons and pizza."', '"West. The ocean."']
        : c === 'china' ? ['"Shanghai or Beijing?"', '"Shanghai. The river at night."', '"Beijing. The hutongs."']
          : ['"Tokyo or Kyoto?"', '"Tokyo. Everything, all the time."', '"Kyoto. Slow mornings."'];
  return [
    { line: '"So. What do you actually do when you\'re not working?"', answers: [['Tell them something true, and a little embarrassing', 1, 'They laugh, then tell you theirs.', 'They tell you something true, and a little embarrassing. You laugh.'], ['"Gym. Grind. Repeat."', 0.45, 'They nod. It sounded like a bio.'], ['"Honestly? Nothing much."', 0.15, 'A pause you both hear.']] },
    { line: local[0], answers: [[local[1], 0.9, 'Ten minutes of friendly arguing.'], [local[2], 0.9, 'They didn\'t expect that. They want to know why.'], ['"I don\'t care, to be honest."', 0.2, 'That ended that.']] },
    { line: '"What\'s something you want that you haven\'t told many people?"', answers: [['Tell them, properly', 1, 'They lean in. The room goes quiet around you.', 'They tell you, properly. You lean in.'], ['"Money. Lots of it."', 0.4, 'They smile. Not quite the answer.'], ['Turn it back on them', 0.6, 'They tell you, then notice you didn\'t.', 'They turn it back on you, smiling.']] },
    { line: '"Okay, worst date you\'ve ever been on. Go."', answers: [['A funny story, not too cruel to the other person', 1, 'They\'re crying laughing.', 'A funny story. You\'re both crying laughing.'], ['"This one, so far." (a joke)', 0.7, 'They laugh, a beat late. Risky, but it landed.'], ['A long rant about your ex', 0.05, 'You can see them deciding something.', 'A long rant about their ex. You check the time.']] },
    { line: '"Do you see yourself staying here, long term?"', answers: [['"Depends what I find here." (looking at them)', 0.95, 'They hold your look a second longer.'], ['"I want to travel. See everywhere."', 0.6, 'Interesting. They ask where first.'], ['"I don\'t plan that far."', 0.35, 'Fair. A little flat.']] },
  ];
}

/** The menu, here: a starter, a main, a drink (each priced per person). */
export function dateMenu(s: LifeState): { label: string; price: number; kind: 'starter' | 'main' | 'drink' }[] {
  const c = hereCountry(s);
  const L = (n: number) => localPrice(s, n, c === 'nigeria' ? 500 : 100);
  const items: Record<string, [string, number, 'starter' | 'main' | 'drink'][]> = {
    nigeria: [['Peppered snail', 9_000, 'starter'], ['Asun', 7_000, 'starter'], ['Seafood okra & pounded yam', 18_000, 'main'], ['Jollof, grilled croaker', 16_000, 'main'], ['Chapman', 4_000, 'drink'], ['A glass of red', 7_000, 'drink']],
    france: [['Soupe à l\'oignon', 2_600, 'starter'], ['Burrata', 3_200, 'starter'], ['Entrecôte frites', 7_000, 'main'], ['Sole meunière', 8_000, 'main'], ['Un verre de Bordeaux', 2_000, 'drink'], ['Kir', 1_800, 'drink']],
    usa: [['Burrata', 2_400, 'starter'], ['Calamari', 2_200, 'starter'], ['Steak frites', 6_400, 'main'], ['Lobster pasta', 7_200, 'main'], ['A glass of wine', 2_400, 'drink'], ['Old fashioned', 3_000, 'drink']],
    china: [['Smashed cucumber', 1_000, 'starter'], ['Xiaolongbao', 1_600, 'starter'], ['Peking duck', 6_000, 'main'], ['Braised pork belly', 4_000, 'main'], ['Jasmine tea', 800, 'drink'], ['A glass of wine', 2_000, 'drink']],
    japan: [['Edamame & karaage', 1_400, 'starter'], ['Sashimi', 2_400, 'starter'], ['Wagyu donburi', 5_000, 'main'], ['Omakase sushi', 8_000, 'main'], ['Highball', 1_200, 'drink'], ['Sake', 1_800, 'drink']],
  };
  return (items[c] ?? items.nigeria).map(([label, n, kind]) => ({ label, price: L(n), kind }));
}

/** Away from a restaurant: what that place sells (the beach bar, the park's kiosk, the bar), each per person. */
export function outingMenu(s: LifeState, spot: OutingSpot): { label: string; price: number; kind: 'starter' | 'main' | 'drink' }[] {
  if (spot === 'restaurant') return dateMenu(s);
  const lagos = hereCountry(s) === 'nigeria', L = (n: number) => localPrice(s, n, lagos ? 500 : 100);
  if (spot === 'beach') return [{ label: 'A cold drink', price: snackPrice(s, 'drink'), kind: 'drink' }, { label: 'A coconut', price: snackPrice(s, 'coconut'), kind: 'drink' },
    ...(lagos ? [{ label: 'Suya', price: snackPrice(s, 'suya'), kind: 'main' as const }] : [])];
  if (spot === 'park') return [{ label: 'A cold zobo', price: 800, kind: 'drink' }, { label: 'Plantain chips and a cold zobo', price: 1_500, kind: 'drink' }];
  return [{ label: 'A cold drink', price: L(1_500), kind: 'drink' }, { label: 'A cocktail', price: L(4_000), kind: 'drink' }, { label: 'Small chops', price: L(3_000), kind: 'main' }];
}

// --- the date itself: a shared session with two seats ----------------------------------------------
//
// Built for two people, not one person and a script. Each SEAT is somebody at the table: you, an NPC, or (with
// the multiplayer backend) another real player. The session holds what's shared (the order, the bill, the log);
// each seat has its own FEEL (how much they're enjoying the other person), moved by what the other says and does.
// Anything that needs the other person's yes (a kiss, "be my partner", "can I see you again") is a REQUEST the
// other seat answers: an NPC from its feel (npcAccepts), a player on their own screen. Nobody gets a yes from a
// dice roll against another human. Life effects (money, closeness, history) are applied per person to their own
// life (finishDate, payShare), never across lives.

export type SeatKind = 'you' | 'npc' | 'player';
export interface DateSeat { name: string; kind: SeatKind; /** how much this person is enjoying the other (0..1) */ feel: number }
export interface DateSession {
  plan: DatePlan;
  seats: [DateSeat, DateSeat];
  log: string[];
}
export type BillChoice = 'pay' | 'split' | 'let';
export type DateEnding = 'hug' | 'kiss' | 'again' | 'partner';
/** what you can ask once dinner's over: to take them home, to come back to yours, to stay the night */
export type AfterRequest = 'drop' | 'home' | 'stay' | 'photo';

/** Sit down: two seats (seat 0 is whoever asked), both a little curious. */
export function startDate(plan: DatePlan, a: Omit<DateSeat, 'feel'>, b: Omit<DateSeat, 'feel'>): DateSession {
  return { plan, seats: [{ ...a, feel: 0.45 }, { ...b, feel: 0.45 }], log: [] };
}
export const otherSeat = (i: number) => (i === 0 ? 1 : 0);
const nameHash = (n: string, salt: number) => [...n].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, salt);

/** `who` walked in like that (firstImpression): it's the OTHER person who notices. */
export function impressed(sess: DateSession, who: number, impression: number) {
  const o = sess.seats[otherSeat(who)];
  o.feel = clamp01(o.feel + (impression - 0.5) * 0.5);
}

/** `asker` raised a topic; the other person's answer was worth `value` (0..1) to them: the asker's feel moves. */
export function answered(sess: DateSession, asker: number, value: number) {
  const a = sess.seats[asker];
  a.feel = clamp01(a.feel + (value - 0.5) * 0.24);
  sess.log.push(`answer:${asker}:${value}`);
}

/** An NPC answering a topic someone raised: mostly decent, sometimes not (its own temperament). Returns the index. */
export function npcAnswer(sess: DateSession, seat: number, answers: [string, number, string, string?][]): number {
  const r = new Rng((nameHash(sess.seats[seat].name, 3) ^ Math.imul(sess.log.length + 1, 977)) >>> 0);
  const sorted = [...answers].sort((x, y) => y[1] - x[1]);
  const roll = r.next();
  return answers.indexOf(roll < 0.6 ? sorted[0] : roll < 0.9 ? sorted[1] : sorted[2]);
}

/** What an NPC says to the bill (by temperament, and where they're from). */
export function npcBill(sess: DateSession, seat: number, country: string): BillChoice {
  const roll = new Rng(nameHash(sess.seats[seat].name, 5)).next();
  return country === 'nigeria' ? (roll < 0.7 ? 'let' : 'split') : roll < 0.45 ? 'split' : roll < 0.75 ? 'pay' : 'let';
}

/**
 * The bill: what each person said, and how it settles. Each seat's SHARE of the total (0, 0.5 or 1), and how each
 * felt about it. Two "I'll pay"s: whoever asked (seat 0) wins the argument. Being left with it stings more in Lagos.
 */
export function settleBill(sess: DateSession, choice: [BillChoice, BillChoice], country: string): { share: [number, number]; line: string } {
  const [a, b] = choice, n0 = sess.seats[0].name.split(' ')[0], n1 = sess.seats[1].name.split(' ')[0];
  const sting = country === 'nigeria' ? 0.14 : 0.06;
  const feel = (i: number, d: number) => { sess.seats[i].feel = clamp01(sess.seats[i].feel + d); };
  let share: [number, number], line: string;
  if (a === 'pay') { share = [1, 0]; line = b === 'pay' ? `You both reach for it. ${n0} wins.` : `${n0} picks up the bill.`; feel(1, 0.06); }
  else if (b === 'pay') { share = [0, 1]; line = `${n1} picks up the bill.`; feel(0, 0.06); if (a === 'let') feel(1, -sting * 0.5); }
  else if (a === 'split' || b === 'split') { share = [0.5, 0.5]; line = '"Split it?" "Of course."'; if (a === 'let') feel(1, -sting * 0.5); if (b === 'let') feel(0, -sting * 0.5); }
  else { share = [0.5, 0.5]; line = 'Neither of you moves. The waiter waits. You split it, a little awkwardly.'; feel(0, -sting); feel(1, -sting); }
  sess.log.push(`bill:${a}:${b}`);
  return { share, line };
}

/** Would an NPC say yes to this request, from how they feel (and how close you already were)? */
export function npcAccepts(sess: DateSession, seat: number, ending: DateEnding | AfterRequest, closeness: number, salt: number): boolean {
  const f = sess.seats[seat].feel;
  const p = ending === 'hug' || ending === 'drop' ? 0.97 : ending === 'photo' ? 0.35 + f : ending === 'again' ? 0.25 + f : ending === 'kiss' ? (f - 0.45) * 2.2
    : ending === 'home' ? (f - 0.5) * 2 + closeness / 300 : ending === 'stay' ? (f - 0.65) * 2.5 + closeness / 400 : (f - 0.5) * 1.8 + closeness / 400;
  return new Rng((salt ^ Math.imul(ending.length + 3, 0x9e3779b1)) >>> 0).chance(clamp01(p));
}

/**
 * After dinner, in THIS life: they let you take them home, came back to yours, or stayed the night (each only
 * with their yes). Closeness, the history; a night over ends with them gone in the morning and a text.
 */
export function afterDate(s: LifeState, name: string, what: AfterRequest | 'evening', accepted: boolean): string[] {
  const p = s.contacts.find((c) => c.name === name), first = name.split(' ')[0];
  if (!accepted) { if (p) p.closeness = Math.max(0, p.closeness - (what === 'stay' ? 2 : 1)); return []; }
  if (what === 'drop') { if (p) p.closeness = Math.min(100, p.closeness + 2); return [`You saw ${first} home.`]; }
  if (what === 'home') { if (p) p.closeness = Math.min(100, p.closeness + 3); s.history.push({ t: s.minutes, text: `${first} came back to yours after dinner.` }); return []; }
  if (what === 'photo') { if (p) p.closeness = Math.min(100, p.closeness + 1); s.history.push({ t: s.minutes, text: `A photo with ${first} after dinner.` }); return []; }
  if (what === 'evening') { if (p) p.closeness = Math.min(100, p.closeness + 3); s.needs.fun = Math.min(100, s.needs.fun + 10); s.needs.social = Math.min(100, s.needs.social + 8); return []; }
  if (p) p.closeness = Math.min(100, p.closeness + 6);
  s.history.push({ t: s.minutes, text: `${first} stayed the night.` });
  return [];
}

/** The morning after: you decide to make it official (they already said yes to the night; this asks them again). */
export function makeOfficial(s: LifeState, name: string): boolean {
  if (!NPC_KIN) return false; // (an NPC partner: archived, kin.ts)
  const p = s.contacts.find((c) => c.name === name);
  if (!p || isPartner(p.relation)) return false;
  p.relation = s.card.sex === 'm' ? 'Girlfriend' : 'Boyfriend';
  p.closeness = Math.min(100, p.closeness + 8);
  s.history.push({ t: s.minutes, text: `Started dating ${name}.` });
  return true;
}

/** Pay your share of the bill from THIS life (declined: you pay nothing, they cover it, and they notice). */
export function payShare(s: LifeState, total: number, share: number, withName: string, what = 'Dinner'): { paid: number; short: boolean } {
  const due = Math.ceil(total * share);
  if (due <= 0) return { paid: 0, short: false };
  if (spend(s, due, `${what} with ${withName.split(' ')[0]}${share < 1 ? ' (split)' : ''}`)) return { paid: due, short: false };
  return { paid: 0, short: true };
}

/**
 * The date is over: apply it to THIS life (`me` is your seat): how the other person felt, what was asked for at
 * the end and whether the other said yes. Closeness, needs, history; an NPC's text in the morning.
 */
export function finishDate(s: LifeState, sess: DateSession, me: number, ending: DateEnding, accepted: boolean, /** you asked for the goodbye (false: they asked, you answered) */ asked = true): { lines: string[]; events: LifeEvent[] } {
  const them = sess.seats[otherSeat(me)], d = sess.plan;
  const p = s.contacts.find((c) => c.name === them.name), pl = them.kind === 'player' ? s.players?.find((x) => x.name === them.name) : undefined;
  const events = advance(s, 20);
  d.state = 'done';
  const k = them.feel, first = them.name.split(' ')[0];
  const lines: string[] = [];
  if (p) { p.closeness = Math.max(0, Math.min(100, p.closeness + (k - 0.45) * 30)); p.seen = s.minutes; }
  s.needs.social = Math.min(100, s.needs.social + 20 + k * 15);
  s.needs.fun = Math.min(100, s.needs.fun + 12 + k * 15);
  s.skills.social = Math.min(100, s.skills.social + 0.4 + k * 0.6);
  nudge(s, 'outgoing', 0.006, events);
  const door = daytime(d.spot) || d.spot === 'lounge' ? 'before you go' : 'at the door';
  if (!asked) { // (they asked; you answered: the same ending, told from your side)
    if (ending === 'hug') lines.push(`${first} hugs you ${door}.`);
    if (ending === 'kiss') lines.push(accepted ? `${first} leans in. So do you.` : `${first} leans in; you turn, and it lands on a cheek.`);
    if (asked && ending === 'again') lines.push(accepted ? `"Can I see you again?" "I'd like that."` : `"Can I see you again?" "Let's see."`);
    if (ending === 'partner') { lines.push(accepted ? `${first} asks you to be their partner. You say yes.` : `${first} asks you to be their partner. "It's too soon."`); if (accepted && pl) pl.relation = 'Partner'; }
  }
  else if (ending === 'hug') lines.push(k > 0.6 ? `A long hug ${door}. ${first} doesn't let go first.` : `A hug ${door}. Friendly.`);
  if (asked && ending === 'kiss') {
    if (accepted) { lines.push(`You lean in. So does ${first}.`); if (p) p.closeness = Math.min(100, p.closeness + 6); }
    else { lines.push(`${first} turns, and it lands on a cheek. "Goodnight," they say, kindly.`); if (p) p.closeness = Math.max(0, p.closeness - 3); }
  }
  if (ending === 'again') lines.push(accepted ? `"Same time next week?" "I'd like that."` : `"Let's see," ${first} says. You know what that means.`);
  if (asked && ending === 'partner') {
    if (p && isPartner(p.relation)) lines.push('"You already have me," they laugh.');
    else if (accepted) { if (p) p.relation = s.card.sex === 'm' ? 'Girlfriend' : 'Boyfriend'; if (pl) pl.relation = 'Partner'; s.history.push({ t: s.minutes, text: `Started dating ${them.name}.` }); lines.push(`"Yes," ${first} says, before you've finished asking.`); }
    else { lines.push(`"It's too soon," ${first} says. "But I had a nice time."`); if (p) p.closeness = Math.max(0, p.closeness - 4); }
  }
  s.history.push({ t: s.minutes, text: `A date with ${them.name} at ${d.venue.split(',')[0]}.` });
  if (them.kind === 'npc') { // (a real player texts for themselves)
    const r = rngFor(s, s.minutes + 77);
    const text = k > 0.75 ? r.pick([`${daytime(d.spot) ? 'Yesterday' : 'Last night'} was really nice. Text me when you're up 🙂`, "I'm still smiling. When are we doing that again?"])
      : k > 0.5 ? r.pick([daytime(d.spot) ? 'Thanks for yesterday. That was fun.' : 'Thanks for last night. That was fun.', 'Got home safe. Had a good time 🙂']) : k > 0.3 ? (!d.spot || d.spot === 'restaurant' ? 'Thanks for dinner.' : 'Thanks for today.') : '';
    if (text) s.inbox.push({ at: (dayOf(s.minutes) + 1) * DAY + 9 * 60, from: them.name, text });
  }
  return { lines, events };
}

/** A new day: missed dates (you never showed) and the morning-after texts. Called by the sim's day roll. */
export function datesDay(s: LifeState, events: LifeEvent[]) {
  for (const d of datesOf(s)) {
    if (d.state !== 'booked' || s.minutes < d.at + 90) continue;
    d.state = 'missed';
    if (d.with === 'player') continue; // (a real person texts for themselves)
    const p = s.contacts.find((c) => c.name === d.name);
    if (p) p.closeness = Math.max(0, p.closeness - 15);
    message(s, d.name, 'I waited an hour. Wow.', events);
  }
  if (s.dates && s.dates.length > 20) s.dates = s.dates.slice(-20);
}

/**
 * Whether they'll actually be there. They live where you met them (your home country): if you're abroad, they're
 * not coming (the plan made sense at home). Otherwise someone you're not close to sometimes flakes. Decided by a
 * seed from the date itself, so asking twice gives the same answer. Players come for themselves: never decided here.
 */
export function wontShow(s: LifeState, name: string, at: number, withPlayer = false): 'away' | 'flaked' | null {
  if (withPlayer) return null;
  if (hereCountry(s) !== homeCountry(s)) return 'away';
  const c = s.contacts.find((x) => x.name === name);
  if (!c || c.closeness >= 40) return null;
  let h = at;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return new Rng(h).next() < 0.14 ? 'flaked' : null;
}
/** They didn't come (you turned up: no blame on you). Their text, when it comes, is the excuse. */
export function dateStoodUp(s: LifeState, d: DatePlan | null, name: string, excuse: string, events: LifeEvent[]) {
  if (d) d.state = 'done';
  message(s, name, excuse, events);
}
